/**
 * Checks if a navigation item path matches the current route location.
 * Handles both pathname and hash fragment matching with proper URL decoding.
 *
 * @param itemPath - The navigation item path (e.g., "/dashboard#monthly")
 * @param currentPathname - The current pathname from useLocation()
 * @param currentHash - The current hash from useLocation() (may include leading #)
 * @returns true if the item path matches the current location
 *
 * @example
 * // Simple pathname match
 * isRouteActive("/dashboard", "/dashboard", "") // true
 *
 * @example
 * // Hash fragment match
 * isRouteActive("/dashboard#monthly", "/dashboard", "#monthly") // true
 * isRouteActive("/dashboard#monthly", "/dashboard", "monthly") // true (handles missing #)
 *
 * @example
 * // URL-encoded hash
 * isRouteActive("/dashboard#my section", "/dashboard", "#my%20section") // true
 */
export const isRouteActive = (itemPath: string | undefined, currentPathname: string, currentHash: string): boolean => {
  if (!itemPath) return false;

  // Split on first # only to separate pathname from hash
  const hashIndex = itemPath.indexOf("#");
  const itemPathname = hashIndex === -1 ? itemPath : itemPath.slice(0, hashIndex);
  const itemHash = hashIndex === -1 ? "" : itemPath.slice(hashIndex + 1);

  // Normalize current hash: remove leading # if present, decode URL encoding
  const normalizedCurrentHash = currentHash
    ? decodeURIComponent(currentHash.startsWith("#") ? currentHash.slice(1) : currentHash)
    : "";

  // Check pathname match
  const isExactMatch = currentPathname === itemPathname;
  const isParentMatch = currentPathname.startsWith(itemPathname + "/");

  if (!isExactMatch && !isParentMatch) return false;

  // If item has no hash but current URL does, don't match (hash indicates different context)
  if (!itemHash) return !normalizedCurrentHash;

  // If item has hash, require exact pathname match and exact hash match
  if (itemHash) {
    if (!isExactMatch) return false;
    const normalizedItemHash = decodeURIComponent(itemHash);
    return normalizedCurrentHash === normalizedItemHash;
  }

  return false;
};
